/*
 * Insights > Cancels (owner, 2026-11-04): how many visits were cancelled or skipped next to done ones, by month, reason,
 * who asked and client, how many were late (under 24 hours) and whether a late fee was noted. Each cancel in the list can
 * get its reason filled in. Owners and managers only (the Insights page needs insights.read).
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { CANCEL_BY_WORDS, CANCEL_REASON_WORDS, type CancelsAnswer } from "@steward/shared";
import { useCancels } from "../api/books";
import { useSession } from "../app/session";
import { cancelLine, useCancelSheet } from "../components/CancelSheet";
import { BarChart } from "../components/Charts";
import { DataTable } from "../components/DataTable";
import { monthName, money, shortDate, todayIn } from "../components/format";
import { Banner, Button, Card, Chip, EmptyState, LoadError, Skeleton, StatusLabel } from "../components/parts";

type Span = "three" | "six" | "year";
const SPANS: Record<Span, string> = { three: "The last 3 months", six: "The last 6 months", year: "This year" };

function spanOf(s: Span, today: string): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  if (s === "year") return { from: `${String(y)}-01-01`, to: today };
  const back = s === "three" ? 2 : 5;
  const m = Number(today.slice(5, 7)) - 1 - back;
  return { from: new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10), to: today };
}

const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

export function Cancels() {
  const { company, can } = useSession();
  const today = todayIn(company.timezone);
  const [span, setSpan] = useState<Span>("three");
  const s = spanOf(span, today);
  const q = useCancels(company.id, s.from, s.to);
  const why = useCancelSheet();
  const n = q.data;
  const editable = can("visit.update");
  return (
    <>
      <p className="muted">
        Visits that were canceled or skipped, next to the ones we did. A late cancel is under 24 hours before the visit.
      </p>
      <div className="chips" aria-label="Days shown">
        {(Object.keys(SPANS) as Span[]).map((v) => (
          <Chip key={v} pressed={span === v} onClick={() => setSpan(v)}>
            {SPANS[v]}
          </Chip>
        ))}
      </div>
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !n ? (
        <Skeleton rows={8} />
      ) : (
        <div className={q.isPlaceholderData ? "refreshing stack" : "stack"}>
          <Overview n={n} />
          <Months n={n} />
          <div className="grid-2 ins-two">
            <Reasons n={n} />
            <Who n={n} />
          </div>
          <Clients n={n} />
          <Card title="Every cancel and skip">
            <DataTable
              title="Cancels"
              rows={n.list}
              rowKey={(r) => r.ref}
              search={(r) => `${r.clientName} ${r.jobName} ${CANCEL_REASON_WORDS[r.info.reason]}`}
              empty={<EmptyState icon="schedule" title="No cancels or skips in these days" />}
              columns={[
                { key: "d", header: "Visit day", sort: (r) => r.date, render: (r) => shortDate(r.date) },
                {
                  key: "c",
                  header: "Client",
                  sort: (r) => r.clientName,
                  render: (r) => (
                    <Link to="/visits/$ref" params={{ ref: r.ref }}>
                      <b>{r.clientName}</b>
                    </Link>
                  ),
                },
                {
                  key: "s",
                  header: "What",
                  sort: (r) => r.status,
                  render: (r) => (
                    <StatusLabel status={r.status === "cancelled" ? "critical" : "inactive"}>
                      {r.status === "cancelled" ? "Canceled" : "Skipped"}
                    </StatusLabel>
                  ),
                },
                { key: "r", header: "Why", sort: (r) => r.info.reason, render: (r) => cancelLine(r.info) },
                {
                  key: "l",
                  header: "Late",
                  sort: (r) => (r.info.late ? 1 : 0),
                  render: (r) => (r.info.late ? <StatusLabel status="warning">Late</StatusLabel> : ""),
                },
                ...(editable
                  ? [
                      {
                        key: "e",
                        header: "",
                        render: (r: CancelsAnswer["list"][number]) => (
                          <Button
                            type={r.info.reason === "not_given" ? "secondary" : "tertiary"}
                            size="small"
                            onClick={() =>
                              why.edit(
                                { ref: r.ref, date: r.date, startTime: null, clientName: r.clientName },
                                r.info,
                                () => void q.refetch(),
                              )
                            }
                          >
                            {r.info.reason === "not_given" ? "Add reason" : "Change"}
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </div>
      )}
      {why.sheet}
    </>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

function Overview({ n }: { n: CancelsAnswer }) {
  const t = n.totals;
  const off = t.cancelled + t.skipped;
  const afterDay = n.list.filter((r) => r.info.hoursBefore != null && r.info.hoursBefore <= -24).length;
  const noReason = n.list.filter((r) => r.info.reason === "not_given").length;
  return (
    <Card title="Overview">
      <div className="ins-kpis">
        <Tile
          label="Cancel rate"
          value={`${String(t.rate)}%`}
          hint={`${plural(off, "visit", "visits")} off out of ${String(off + t.done)}`}
        />
        <Tile label="Canceled" value={String(t.cancelled)} hint={plural(t.skipped, "more was skipped", "more were skipped")} />
        <Tile label="Done" value={String(t.done)} hint="Visits we did" />
        <Tile label="Late cancels" value={String(t.late)} hint="Under 24 hours before" />
        <Tile
          label="Late fee noted"
          value={String(t.lateWithFee)}
          hint={t.lateWithFee ? `${money(t.feeCents)} in fees` : t.late ? "None of the late ones" : "No late ones"}
        />
      </div>
      {afterDay ? (
        <Banner type="warning">
          {plural(afterDay, "visit was", "visits were")} taken off after the visit day. Those may be visits that never happened
          and were cleaned up later, not real cancels.
        </Banner>
      ) : null}
      {noReason ? (
        <Banner type="notice">
          {plural(noReason, "cancel has", "cancels have")} no reason yet. Add it from the list at the bottom.
        </Banner>
      ) : null}
    </Card>
  );
}

function Months({ n }: { n: CancelsAnswer }) {
  if (!n.months.length) return null;
  return (
    <Card title="By month">
      <BarChart
        labels={n.months.map((m) => monthName(`${m.month}-01`).slice(0, 3))}
        tipLabels={n.months.map((m) => `${monthName(`${m.month}-01`)}, ${String(m.rate)}% off`)}
        series={[
          { name: "Done", values: n.months.map((m) => m.done), tone: "soft" },
          { name: "Canceled or skipped", values: n.months.map((m) => m.cancelled + m.skipped), tone: "main" },
        ]}
        format={(v) => String(v)}
        height={200}
      />
      <DataTable
        title="Months"
        rows={n.months}
        rowKey={(m) => m.month}
        columns={[
          { key: "m", header: "Month", render: (m) => monthName(`${m.month}-01`) },
          { key: "d", header: "Done", align: "right", render: (m) => <span className="num">{m.done}</span> },
          { key: "c", header: "Canceled", align: "right", render: (m) => <span className="num">{m.cancelled}</span> },
          { key: "s", header: "Skipped", align: "right", render: (m) => <span className="num">{m.skipped}</span> },
          { key: "l", header: "Late", align: "right", render: (m) => <span className="num">{m.late}</span> },
          { key: "r", header: "Rate", align: "right", render: (m) => <span className="num">{m.rate}%</span> },
        ]}
      />
    </Card>
  );
}

function Reasons({ n }: { n: CancelsAnswer }) {
  return (
    <Card title="Why">
      <DataTable
        title="Reasons"
        rows={n.reasons}
        rowKey={(r) => r.reason}
        empty={<EmptyState icon="schedule" title="Nothing cancelled" />}
        columns={[
          { key: "r", header: "Reason", render: (r) => CANCEL_REASON_WORDS[r.reason] },
          { key: "o", header: "Count", align: "right", render: (r) => <span className="num">{r.off}</span> },
          { key: "l", header: "Late", align: "right", render: (r) => <span className="num">{r.late}</span> },
        ]}
      />
    </Card>
  );
}

function Who({ n }: { n: CancelsAnswer }) {
  return (
    <Card title="Who asked">
      <DataTable
        title="Who asked"
        rows={n.by}
        rowKey={(r) => r.by}
        empty={<EmptyState icon="schedule" title="Nothing cancelled" />}
        columns={[
          {
            key: "b",
            header: "Who",
            render: (r) =>
              r.by === "unknown" ? "Nobody said" : r.by === "client" ? "The client" : `${CANCEL_BY_WORDS.us}, the company`,
          },
          { key: "o", header: "Count", align: "right", render: (r) => <span className="num">{r.off}</span> },
          { key: "l", header: "Late", align: "right", render: (r) => <span className="num">{r.late}</span> },
        ]}
      />
    </Card>
  );
}

function Clients({ n }: { n: CancelsAnswer }) {
  return (
    <Card title="Clients who cancel most">
      <DataTable
        title="Clients"
        rows={n.clients}
        rowKey={(c) => c.clientId}
        empty={<EmptyState icon="clients" title="No client cancelled in these days" />}
        columns={[
          {
            key: "n",
            header: "Client",
            sort: (c) => c.name,
            render: (c) => (
              <Link to="/clients/$id" params={{ id: c.clientId }}>
                <b>{c.name}</b>
              </Link>
            ),
          },
          { key: "o", header: "Off", align: "right", sort: (c) => c.off, render: (c) => <span className="num">{c.off}</span> },
          { key: "d", header: "Done", align: "right", sort: (c) => c.done, render: (c) => <span className="num">{c.done}</span> },
          { key: "l", header: "Late", align: "right", sort: (c) => c.late, render: (c) => <span className="num">{c.late}</span> },
          { key: "r", header: "Last reason", render: (c) => CANCEL_REASON_WORDS[c.lastReason] },
        ]}
      />
    </Card>
  );
}
